import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { useAuth } from "../../../../app/providers/AuthProvider";
import {
  SIDEBAR_COLOR_MODE,
  setStoredSidebarDarkMode,
  useSidebarDarkMode,
} from "../../useAppLayoutAppearance";
import MenuContent, { APP_SHELL } from "./MenuContent";

export default function SideMenu() {
  const { isAuthenticated } = useAuth();
  const sidebarDark = useSidebarDarkMode();
  const [compact, setCompact] = React.useState(false);
  const [compactContent, setCompactContent] = React.useState(false);
  const [labelVisible, setLabelVisible] = React.useState(true);
  const transitionTimer = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (transitionTimer.current) {
        window.clearTimeout(transitionTimer.current);
      }
    };
  }, []);

  if (!isAuthenticated) return null;

  const width = compact
    ? APP_SHELL.sidebarCompactWidth
    : APP_SHELL.sidebarExpandedWidth;

  function toggleCompact() {
    if (transitionTimer.current) {
      window.clearTimeout(transitionTimer.current);
    }

    if (compact) {
      setCompactContent(false);
      setCompact(false);
      transitionTimer.current = window.setTimeout(() => {
        setLabelVisible(true);
      }, 120);
      return;
    }

    setLabelVisible(false);
    setCompact(true);
    transitionTimer.current = window.setTimeout(() => {
      setCompactContent(true);
    }, 210);
  }

  return (
    <Box
      component="aside"
      sx={{
        display: { xs: "none", md: "block" },
        flexBasis: width,
        flexGrow: 0,
        flexShrink: 0,
        height: "100vh",
        maxWidth: width,
        minWidth: width,
        position: "sticky",
        top: 0,
        transition: [
          "flex-basis 260ms cubic-bezier(0.2, 0, 0, 1)",
          "max-width 260ms cubic-bezier(0.2, 0, 0, 1)",
          "min-width 260ms cubic-bezier(0.2, 0, 0, 1)",
          "width 260ms cubic-bezier(0.2, 0, 0, 1)",
        ].join(", "),
        width,
        zIndex: (theme) => theme.zIndex.drawer,
      }}
    >
      <MenuContent
        compact={compactContent}
        labelVisible={labelVisible}
        rail={compact}
        sidebarDark={sidebarDark}
        onToggleDarkMode={() => {
          if (!SIDEBAR_COLOR_MODE.toggleEnabled) return;
          setStoredSidebarDarkMode(!sidebarDark);
        }}
      />

      <Tooltip title={compact ? "Expandir menu" : "Recolher menu"} placement="right">
        <ButtonBase
          type="button"
          aria-label={compact ? "Expandir menu" : "Recolher menu"}
          onClick={toggleCompact}
          sx={(theme) => ({
            alignItems: "center",
            bgcolor: sidebarDark
              ? theme.palette.custom.gray[800]
              : theme.palette.background.paper,
            border: `1px solid ${
              sidebarDark ? theme.palette.custom.gray[700] : theme.palette.border.default
            }`,
            borderRadius: "50%",
            color: sidebarDark ? theme.palette.custom.gray[300] : theme.palette.text.secondary,
            display: "flex",
            height: 28,
            justifyContent: "center",
            position: "absolute",
            right: -14,
            top: 18,
            width: 28,
            zIndex: theme.zIndex.drawer + 1,
            "&:hover": {
              bgcolor: sidebarDark
                ? theme.palette.custom.gray[700]
                : theme.palette.action.hover,
              color: sidebarDark ? theme.palette.custom.gray[50] : theme.palette.text.primary,
            },
          })}
        >
          {compact ? (
            <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
          ) : (
            <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
          )}
        </ButtonBase>
      </Tooltip>
    </Box>
  );
}
